// src/pages/.../ManageMembers/components/MembersList.jsx

import React from "react";

import {
  FaEdit,
  FaFolderOpen,
  FaLayerGroup,
  FaTrash,
} from "react-icons/fa";

import { GROUP_COLOR_MAP } from "../data/manageMembers.data";

import {
  cn,
  getAdherentDojoNames,
  getAdherentGroups,
  getInitials,
} from "../utils/manageMembers.utils";

function GroupPills({ adherent }) {
  const groups = getAdherentGroups(adherent);

  if (!groups.length) {
    return (
      <span className="text-sm text-slate-400">
        Aucun
      </span>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {groups.map((group) => (
        <span
          key={group.id}
          className={cn(
            "inline-flex rounded-full px-3 py-1 text-xs font-semibold",
            GROUP_COLOR_MAP[group.color_groupe] ||
              "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
          )}
        >
          {group.nom_groupe}
        </span>
      ))}
    </div>
  );
}

function DojoPills({ adherent }) {
  const dojoNames = getAdherentDojoNames(adherent);

  if (!dojoNames.length) {
    return (
      <span className="text-sm text-slate-400">
        Aucun
      </span>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {dojoNames.map((dojoName) => (
        <span
          key={dojoName}
          className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 dark:bg-blue-500/10 dark:text-blue-300"
        >
          {dojoName}
        </span>
      ))}
    </div>
  );
}

function MemberActions({
  adherent,
  demoMode,
  submitting,
  onManageGroups,
  onEdit,
  onDelete,
}) {
  return (
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        onClick={() => onManageGroups(adherent)}
        disabled={demoMode}
        className={cn(
          "inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-white transition",
          demoMode
            ? "cursor-not-allowed bg-slate-400 dark:bg-slate-700"
            : "bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600",
        )}
      >
        <FaLayerGroup />
        Groupes
      </button>

      <button
        type="button"
        onClick={() => onEdit(adherent)}
        disabled={demoMode}
        className={cn(
          "inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-white transition",
          demoMode
            ? "cursor-not-allowed bg-slate-400 dark:bg-slate-700"
            : "bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600",
        )}
      >
        <FaEdit />
        Modifier
      </button>

      <button
        type="button"
        onClick={() => onDelete(adherent.id)}
        disabled={demoMode || submitting}
        className={cn(
          "inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-white transition",
          demoMode || submitting
            ? "cursor-not-allowed bg-slate-400 dark:bg-slate-700"
            : "bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600",
        )}
      >
        <FaTrash />
        Supprimer
      </button>
    </div>
  );
}

export default function MembersList({
  adherents,
  demoMode,
  submitting,
  onManageGroups,
  onEdit,
  onDelete,
}) {
  return (
    <>
      <section className="mt-6 hidden rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 lg:block">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-2xl font-black text-slate-900 dark:text-slate-100">
            Liste des adhérents
          </h2>

          <span className="text-sm text-slate-500 dark:text-slate-400">
            {adherents.length} résultat(s)
          </span>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-slate-800">
          <table className="w-full min-w-[1050px] border-collapse">
            <thead>
              <tr className="bg-slate-100 text-left text-sm dark:bg-slate-800">
                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Adhérent
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Contact
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Groupes
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Lieux
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Statut
                </th>

                <th className="px-4 py-3 text-center font-bold text-slate-700 dark:text-slate-200">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {adherents.length > 0 ? (
                adherents.map((adherent) => (
                  <tr
                    key={adherent.id}
                    className="border-t border-slate-200 align-top transition hover:bg-slate-50/70 dark:border-slate-800 dark:hover:bg-slate-800/30"
                  >
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        {adherent.photo_url ? (
                          <img
                            src={adherent.photo_url}
                            alt={`${adherent.prenom} ${adherent.nom}`}
                            className="h-12 w-12 rounded-full object-cover"
                          />
                        ) : (
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-sm font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-200">
                            {getInitials(adherent)}
                          </div>
                        )}

                        <div>
                          <p className="font-bold text-slate-900 dark:text-slate-100">
                            {adherent.nom}
                          </p>

                          <p className="text-sm text-slate-600 dark:text-slate-400">
                            {adherent.prenom}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-4 text-sm text-slate-700 dark:text-slate-300">
                      <div>{adherent.email || "—"}</div>
                      <div className="mt-1">
                        {adherent.tel || "—"}
                      </div>
                    </td>

                    <td className="px-4 py-4">
                      <GroupPills adherent={adherent} />
                    </td>

                    <td className="px-4 py-4">
                      <DojoPills adherent={adherent} />
                    </td>

                    <td className="px-4 py-4">
                      <span className="inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                        {adherent.statut_inscription ||
                          "Inscrit"}
                      </span>
                    </td>

                    <td className="px-4 py-4">
                      <div className="flex justify-center">
                        <MemberActions
                          adherent={adherent}
                          demoMode={demoMode}
                          submitting={submitting}
                          onManageGroups={onManageGroups}
                          onEdit={onEdit}
                          onDelete={onDelete}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    className="px-4 py-10 text-center text-slate-500 dark:text-slate-400"
                  >
                    Aucun adhérent trouvé.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-6 space-y-4 lg:hidden">
        {adherents.length > 0 ? (
          adherents.map((adherent) => (
            <article
              key={adherent.id}
              className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900"
            >
              <div className="flex items-start gap-4">
                {adherent.photo_url ? (
                  <img
                    src={adherent.photo_url}
                    alt={`${adherent.prenom} ${adherent.nom}`}
                    className="h-16 w-16 shrink-0 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-slate-100 text-lg font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-200">
                    {getInitials(adherent)}
                  </div>
                )}

                <div className="min-w-0 flex-1">
                  <h3 className="break-words text-xl font-black text-slate-900 dark:text-slate-100">
                    {adherent.prenom} {adherent.nom}
                  </h3>

                  <p className="mt-1 break-all text-sm text-slate-600 dark:text-slate-400">
                    {adherent.email ||
                      "Email non renseigné"}
                  </p>

                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                    {adherent.tel ||
                      "Téléphone non renseigné"}
                  </p>
                </div>
              </div>

              <div className="mt-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Groupes
                </p>

                <div className="mt-2">
                  <GroupPills adherent={adherent} />
                </div>
              </div>

              <div className="mt-5">
                <MemberActions
                  adherent={adherent}
                  demoMode={demoMode}
                  submitting={submitting}
                  onManageGroups={onManageGroups}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              </div>
            </article>
          ))
        ) : (
          <div className="rounded-[24px] border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
              <FaFolderOpen className="text-2xl" />
            </div>

            <h3 className="mt-4 text-xl font-black text-slate-900 dark:text-slate-100">
              Aucun adhérent trouvé
            </h3>
          </div>
        )}
      </section>
    </>
  );
}